import type { Currency } from './currency.js';
import type { DonationStatus, PaymentStatus } from './donation-status.js';
import { normalizeDonorName } from './donor-match.js';
import { type ConversionRate, convertBetween, type Money, money } from './money.js';
import { lineRemainder, matchClosedReason } from './reconciliation.js';
import {
  correctSingleSymbol,
  extractReferenceCodes,
  normalizeReferenceCode,
  referenceCodeTokens,
} from './reference-code.js';

export const RECONCILIATION_METHODS = [
  'reference_exact',
  'reference_multi',
  'reference_amount_mismatch',
  'reference_corrected',
  'amount_date',
  'manual',
  'provider',
] as const;
export type ReconciliationMethod = (typeof RECONCILIATION_METHODS)[number];

export const RECONCILIATION_APPROVAL_REASONS = ['cross_currency'] as const;
export type ReconciliationApprovalReason = (typeof RECONCILIATION_APPROVAL_REASONS)[number];

export type MatchLine = {
  readonly id: string;
  readonly organizationId: string;
  readonly amount: Money;
  readonly allocatedMinor: bigint;
  readonly bookedOn: string;
  readonly description: string | null;
  readonly counterpartyName: string | null;
};

/**
 * A donation the candidate query already narrowed: same organization, a
 * matchable status, a payment status that can still receive money, and an
 * outstanding above zero. The ranker judges the reference, the amount and the
 * date, never the permissions.
 */
export type MatchCandidate = {
  readonly donationId: string;
  readonly referenceCode: string;
  readonly outstanding: Money;
  readonly donatedOn: string;
  readonly paymentMethod: string;
  readonly donorName: string | null;
};

export type ProposedAllocation = {
  readonly donationId: string;
  readonly transactionAmountMinor: bigint;
};

export type MatchProposal = {
  readonly accountTransactionId: string;
  readonly method: ReconciliationMethod;
  readonly score: number;
  readonly bulkConfirmable: boolean;
  readonly approvalReason: ReconciliationApprovalReason | null;
  readonly allocations: readonly ProposedAllocation[];
};

/** How far before a line's `booked_on` a donation may be dated and still be its amount-and-date match. */
export const AMOUNT_DATE_WINDOW_BEFORE_DAYS = 30;
/** How far after it — a transfer booked before the donation was entered. */
export const AMOUNT_DATE_WINDOW_AFTER_DAYS = 3;
const AMOUNT_DATE_BASE_SCORE = 40;
const AMOUNT_DATE_NAME_BONUS = 30;
const CROSS_CURRENCY_SCORE = 50;

/**
 * The rate rows of the one day a cross-currency proposal converts on — the
 * shape `ExchangeRateRepository.sharedBefore` answers with. TRY has no row.
 */
export type SharedConversionRates = {
  readonly rateDate: string;
  readonly rates: readonly ConversionRate[];
};

/**
 * Why a donation is offered against a line: the text names its code, names it
 * one symbol off, or its outstanding is exactly what is left of the line.
 */
export const CANDIDATE_READINGS = ['reference', 'corrected', 'amount_date'] as const;
export type CandidateReading = (typeof CANDIDATE_READINGS)[number];

/**
 * Why a donation a line names cannot take the line's money. Shown to a person
 * beside the candidates and never offered, because each is money an operator
 * must decide about rather than one the engine may route —
 * docs/decisions/0023-donation-status-axes.md leaves it unmatched.
 */
export const INFORMATION_HIT_REASONS = [
  'organization_not_linked',
  'other_organization',
  'cancelled',
  'settled',
  'refunded',
  'not_charged',
  'nothing_outstanding',
  'card',
  'manual_payment',
] as const;
export type InformationHitReason = (typeof INFORMATION_HIT_REASONS)[number];

export type NamedDonation = {
  readonly organizationId: string | null;
  /** Whether the donation's institution is linked to the line's organization; read only when it has no organization. */
  readonly linkedToLineOrganization: boolean;
  readonly status: DonationStatus;
  readonly paymentStatus: PaymentStatus;
  readonly paymentMethod: string;
  readonly outstandingMinor: bigint;
  /**
   * A bank transfer or cheque somebody typed by hand already holds part of it.
   * The line may be that payment's own money arriving, and matching it again
   * would count it twice — docs/decisions/0100.
   */
  readonly heldByManualBankPayment?: boolean;
};

/**
 * The reason a donation a line names is information rather than a candidate,
 * or null when it is a candidate. Checked in the order an operator would act
 * on: which legal entity the money belongs to first, then a payment typed by
 * hand that this line may be the bank side of, then the record's own state,
 * then the money already against it.
 */
export function informationReasonFor(
  donation: NamedDonation,
  lineOrganizationId: string,
): InformationHitReason | null {
  if (donation.organizationId === null) {
    if (!donation.linkedToLineOrganization) return 'organization_not_linked';
  } else if (donation.organizationId !== lineOrganizationId) {
    return 'other_organization';
  }
  if (donation.heldByManualBankPayment === true) return 'manual_payment';
  const closed = matchClosedReason(donation);
  if (closed !== null) return closed;
  if (donation.outstandingMinor <= 0n) return 'nothing_outstanding';
  if (donation.paymentMethod === 'card') return 'card';
  return null;
}

/**
 * The best proposal for one line, exactly the tier table in
 * docs/plans/05-finance-reconciliation.md §5.5.
 *
 * **A proposal is data, never a row.** Nothing is stored, nothing is reserved,
 * and the writer re-derives every amount under a lock — so a proposal cannot go
 * stale and needs no auditing until a person confirms it.
 *
 * `null` means the engine has nothing to offer, which is also the answer when
 * two candidates qualify on amount and date alone: guessing between them is how
 * money lands in the wrong donation.
 *
 * **The text is read whole before a tier is chosen**, because T1 and T1b are
 * bulk-confirmed by one person and must not be reachable from a description
 * that also says something else:
 *
 * - every valid code the text names must be a candidate — a code on a
 *   cancelled, settled, card or foreign donation is a reason for a person to
 *   look, not a code to step over while the others settle;
 * - a code-shaped token one symbol away from a candidate the valid codes do not
 *   already name makes the reading ambiguous;
 * - the typo tier and the amount-and-date tier are reached only when the text
 *   names no valid code, and amount-and-date only when no typo resolves either,
 *   because "no code" is that tier's rule.
 *
 * `rates` are the line's rate day (0019's amended rule), needed only for a
 * cross-currency proposal. Without them none is made, because the writer would
 * refuse it `RECONCILIATION_RATE_UNAVAILABLE`.
 */
export function rankCandidates(
  line: MatchLine,
  candidates: readonly MatchCandidate[],
  rates: SharedConversionRates | null = null,
): MatchProposal | null {
  const remainder = lineRemainder(line);
  if (remainder.amountMinor <= 0n) return null;

  const usable = candidates.filter(
    (candidate) => candidate.outstanding.amountMinor > 0n && candidate.paymentMethod !== 'card',
  );
  const byCode = new Map(
    usable.map((candidate) => [normalizeReferenceCode(candidate.referenceCode), candidate]),
  );

  const texts = lineTexts(line);
  const named = namedCodes(texts);
  const corrected = correctedCandidates(texts, byCode);

  if (named.length > 0) {
    const resolved = named.map((code) => byCode.get(code));
    if (resolved.some((candidate) => candidate === undefined)) return null;
    const exact = resolved as MatchCandidate[];
    if ([...corrected].some((candidate) => !exact.includes(candidate))) return null;
    if (exact.length > 1) return multiReferenceProposal(line, remainder, exact);
    return referenceProposal(line, remainder, exact[0] as MatchCandidate, rates);
  }

  if (corrected.size > 1) return null;
  if (corrected.size === 1) {
    return correctedProposal(line, remainder, [...corrected][0] as MatchCandidate);
  }

  return amountAndDateProposal(line, remainder, usable);
}

/**
 * The description and the counterparty name are read apart: the reader folds
 * spaces away, so reading them joined would splice the end of one field onto
 * the start of the other and find a code neither contains.
 */
function lineTexts(line: Pick<MatchLine, 'description' | 'counterpartyName'>): string[] {
  return [line.description, line.counterpartyName].filter(
    (text): text is string => text !== null && text !== '',
  );
}

function namedCodes(texts: readonly string[]): string[] {
  return [...new Set(texts.flatMap((text) => extractReferenceCodes(text)))];
}

function corrections(texts: readonly string[]): Set<string> {
  const found = new Set<string>();
  for (const text of texts) {
    for (const token of referenceCodeTokens(text)) {
      for (const correction of correctSingleSymbol(token)) found.add(correction);
    }
  }
  return found;
}

/** The valid reference codes a line's description and counterparty name spell out, in order. */
export function referenceCodesNamed(
  line: Pick<MatchLine, 'description' | 'counterpartyName'>,
): string[] {
  return namedCodes(lineTexts(line));
}

function referenceProposal(
  line: MatchLine,
  remainder: Money,
  candidate: MatchCandidate,
  rates: SharedConversionRates | null,
): MatchProposal | null {
  if (candidate.outstanding.currency !== remainder.currency) {
    return crossCurrencyProposal(line, remainder, candidate, rates);
  }

  if (candidate.outstanding.amountMinor === remainder.amountMinor) {
    return {
      accountTransactionId: line.id,
      method: 'reference_exact',
      score: 100,
      bulkConfirmable: true,
      approvalReason: null,
      allocations: [
        { donationId: candidate.donationId, transactionAmountMinor: remainder.amountMinor },
      ],
    };
  }

  const consumed =
    remainder.amountMinor < candidate.outstanding.amountMinor
      ? remainder.amountMinor
      : candidate.outstanding.amountMinor;
  return {
    accountTransactionId: line.id,
    method: 'reference_amount_mismatch',
    score: 80,
    bulkConfirmable: false,
    approvalReason: null,
    allocations: [{ donationId: candidate.donationId, transactionAmountMinor: consumed }],
  };
}

/** T3 holds T1's amount rule and never crosses a currency: a guessed code is not a code that resolves. */
function correctedProposal(
  line: MatchLine,
  remainder: Money,
  candidate: MatchCandidate,
): MatchProposal | null {
  if (candidate.outstanding.currency !== remainder.currency) return null;
  if (candidate.outstanding.amountMinor !== remainder.amountMinor) return null;
  return {
    accountTransactionId: line.id,
    method: 'reference_corrected',
    score: 60,
    bulkConfirmable: false,
    approvalReason: null,
    allocations: [
      { donationId: candidate.donationId, transactionAmountMinor: remainder.amountMinor },
    ],
  };
}

/**
 * A code that resolves across currencies, in T1 or T2 shape: the whole line
 * when its conversion fits the outstanding, otherwise the largest part of it
 * whose conversion does, so the rest stays visibly unallocated as T2 leaves an
 * overpayment. Converted on the one shared rate day, and stored waiting for a
 * second person — docs/decisions/0044-a-match-is-an-allocation-a-person-confirms.md §3.
 */
function crossCurrencyProposal(
  line: MatchLine,
  remainder: Money,
  candidate: MatchCandidate,
  rates: SharedConversionRates | null,
): MatchProposal | null {
  if (rates === null) return null;
  const lineRate = rateOf(remainder.currency, rates);
  const donationRate = rateOf(candidate.outstanding.currency, rates);
  if (lineRate === undefined || donationRate === undefined) return null;

  const credited = (lineSideMinor: bigint) =>
    convertBetween(
      money(lineSideMinor, remainder.currency),
      lineRate,
      donationRate,
      candidate.outstanding.currency,
    ).amountMinor;
  const consumed = largestLineSideWithin(
    remainder.amountMinor,
    candidate.outstanding.amountMinor,
    credited,
  );
  if (consumed <= 0n || credited(consumed) <= 0n) return null;

  return {
    accountTransactionId: line.id,
    method: 'reference_exact',
    score: CROSS_CURRENCY_SCORE,
    bulkConfirmable: false,
    approvalReason: 'cross_currency',
    allocations: [{ donationId: candidate.donationId, transactionAmountMinor: consumed }],
  };
}

function rateOf(
  currency: Currency,
  rates: SharedConversionRates,
): ConversionRate | null | undefined {
  if (currency === 'TRY') return null;
  return rates.rates.find((rate) => rate.currency === currency);
}

/**
 * The largest line-side amount, up to `available`, whose conversion does not
 * exceed `limit`. Conversion is monotonic in the amount, so a binary search
 * over integer minor units finds it exactly — no inverse rate, and therefore
 * no second rounding.
 */
export function largestLineSideWithin(
  available: bigint,
  limit: bigint,
  convert: (lineSideMinor: bigint) => bigint,
): bigint {
  if (available <= 0n || limit <= 0n) return 0n;
  if (convert(available) <= limit) return available;
  let low = 0n;
  let high = available;
  while (high - low > 1n) {
    const middle = (low + high) / 2n;
    if (convert(middle) <= limit) low = middle;
    else high = middle;
  }
  return low;
}

function multiReferenceProposal(
  line: MatchLine,
  remainder: Money,
  resolved: readonly MatchCandidate[],
): MatchProposal | null {
  const sameCurrency = resolved.every(
    (candidate) => candidate.outstanding.currency === remainder.currency,
  );
  if (!sameCurrency) return null;

  const total = resolved.reduce((acc, candidate) => acc + candidate.outstanding.amountMinor, 0n);
  if (total !== remainder.amountMinor) return null;

  return {
    accountTransactionId: line.id,
    method: 'reference_multi',
    score: 95,
    bulkConfirmable: true,
    approvalReason: null,
    allocations: resolved.map((candidate) => ({
      donationId: candidate.donationId,
      transactionAmountMinor: candidate.outstanding.amountMinor,
    })),
  };
}

/**
 * Every donation reference code a line could be read as naming: each valid code
 * in its description or counterparty name, and every single-symbol correction
 * of a failing token. The writer narrows its candidate query to these codes
 * before it re-ranks a claimed tier, so a claim is judged against every donation
 * the ranker would have seen rather than only the ones the client allocated.
 */
export function referenceCodesMentioned(
  line: Pick<MatchLine, 'description' | 'counterpartyName'>,
): string[] {
  const texts = lineTexts(line);
  return [...new Set([...namedCodes(texts), ...corrections(texts)])].sort();
}

/**
 * Whether a claimed tier is what the ranker itself proposes for this line over
 * these candidates, allocation for allocation. A claim the server cannot
 * reproduce is stored as `manual`, so a stored tier always means the engine
 * would have said the same.
 */
export function proposalReproduces(
  proposal: MatchProposal | null,
  claimed: ReconciliationMethod,
  allocations: readonly ProposedAllocation[],
): proposal is MatchProposal {
  if (proposal === null || proposal.method !== claimed) return false;
  if (proposal.allocations.length !== allocations.length) return false;
  const claimedById = new Map(
    allocations.map((allocation) => [allocation.donationId, allocation.transactionAmountMinor]),
  );
  return proposal.allocations.every(
    (allocation) => claimedById.get(allocation.donationId) === allocation.transactionAmountMinor,
  );
}

/**
 * Why each candidate is offered against a line, for a person choosing among
 * them: the text names its code, names it one symbol off, or — in the line's
 * currency — its outstanding is exactly what is left of the line and it was
 * donated inside the amount-and-date window. A candidate with none of these is
 * absent from the answer: it came from a manual search, not from the line.
 */
export function readCandidates(
  line: MatchLine,
  candidates: readonly MatchCandidate[],
): Map<string, CandidateReading> {
  const remainder = lineRemainder(line);
  const texts = lineTexts(line);
  const named = new Set(namedCodes(texts));
  const corrected = corrections(texts);

  const readings = new Map<string, CandidateReading>();
  for (const candidate of candidates) {
    const code = normalizeReferenceCode(candidate.referenceCode);
    if (named.has(code)) {
      readings.set(candidate.donationId, 'reference');
    } else if (corrected.has(code)) {
      readings.set(candidate.donationId, 'corrected');
    } else if (
      candidate.paymentMethod !== 'card' &&
      candidate.outstanding.currency === remainder.currency &&
      candidate.outstanding.amountMinor === remainder.amountMinor &&
      withinWindow(candidate.donatedOn, line.bookedOn)
    ) {
      readings.set(candidate.donationId, 'amount_date');
    }
  }
  return readings;
}

/** Every candidate a single-symbol correction of a failing code-shaped token resolves to. */
function correctedCandidates(
  texts: readonly string[],
  byCode: ReadonlyMap<string, MatchCandidate>,
): Set<MatchCandidate> {
  const found = new Set<MatchCandidate>();
  for (const correction of corrections(texts)) {
    const candidate = byCode.get(correction);
    if (candidate) found.add(candidate);
  }
  return found;
}

function amountAndDateProposal(
  line: MatchLine,
  remainder: Money,
  candidates: readonly MatchCandidate[],
): MatchProposal | null {
  const matching = candidates.filter(
    (candidate) =>
      candidate.outstanding.currency === remainder.currency &&
      candidate.outstanding.amountMinor === remainder.amountMinor &&
      withinWindow(candidate.donatedOn, line.bookedOn),
  );
  if (matching.length !== 1) return null;

  const only = matching[0] as MatchCandidate;
  return {
    accountTransactionId: line.id,
    method: 'amount_date',
    score: amountDateScore(only, line),
    bulkConfirmable: false,
    approvalReason: null,
    allocations: [{ donationId: only.donationId, transactionAmountMinor: remainder.amountMinor }],
  };
}

/**
 * How far the ranking trusts an amount-and-date match: the floor when nothing
 * but the amount agrees, rising to the ceiling when every word of the donor's
 * name appears in the line's counterparty or description.
 */
export function amountDateScore(candidate: MatchCandidate, line: MatchLine): number {
  const donorTokens = tokensOf(candidate.donorName);
  if (donorTokens.length === 0) return AMOUNT_DATE_BASE_SCORE;
  const lineTokens = new Set(tokensOf(`${line.counterpartyName ?? ''} ${line.description ?? ''}`));
  const hits = donorTokens.filter((token) => lineTokens.has(token)).length;
  const overlap = hits / donorTokens.length;
  return AMOUNT_DATE_BASE_SCORE + Math.round(AMOUNT_DATE_NAME_BONUS * overlap);
}

function tokensOf(value: string | null): string[] {
  const normalized = normalizeDonorName(value);
  if (normalized === null) return [];
  return normalized.split(' ').filter((token) => token.length > 1);
}

const DAY_MS = 86_400_000;

function withinWindow(donatedOn: string, bookedOn: string): boolean {
  const donated = Date.parse(`${donatedOn}T00:00:00Z`);
  const booked = Date.parse(`${bookedOn}T00:00:00Z`);
  if (Number.isNaN(donated) || Number.isNaN(booked)) return false;
  return (
    donated >= booked - AMOUNT_DATE_WINDOW_BEFORE_DAYS * DAY_MS &&
    donated <= booked + AMOUNT_DATE_WINDOW_AFTER_DAYS * DAY_MS
  );
}
